import {useEffect, useMemo, useState} from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {X} from 'lucide-react'
import {formatTopicRequestAuthor, formatTopicRequestLabel} from '../data/requestIdentity'
import {searchTopicReferences, type TopicReference} from '../services/topicsApi'

type Props = {
  topicId: string
  kind: 'familias' | 'segmentacoes'
  label: string
  value: TopicReference[]
  onChange: (references: TopicReference[]) => void
  required?: boolean
  multiple?: boolean
  error?: string
  disabled?: boolean
}

function referenceSource(reference: TopicReference) {
  return reference.source === 'briefing' ? 'Briefing de Promoções' : 'Solicitação de criação'
}

export function TopicReferenceSummary({reference}: {reference: TopicReference}) {
  return (
    <>
      <Typography
        variant='subtitle2'
        fontWeight={600}
        sx={{pr: 3.5, overflowWrap: 'anywhere', lineHeight: 1.5}}
      >
        {reference.name}
      </Typography>
      <Box
        sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mt: 1.5}}
      >
        {[
          reference.source === 'solicitacao'
            ? ['Solicitação', formatTopicRequestLabel(reference)]
            : ['Referência local', reference.referenceId],
          ...(reference.source === 'solicitacao'
            ? [['Realizada por', formatTopicRequestAuthor(reference)]]
            : []),
          ['Origem', referenceSource(reference)],
          ...(reference.industry ? [['Indústria', reference.industry]] : []),
        ].map(([label, value]) => (
          <Box key={label} sx={{minWidth: 0}}>
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
            <Typography variant='body2' fontWeight={500} sx={{overflowWrap: 'anywhere'}}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  )
}

export default function TopicReferencePicker({
  topicId,
  kind,
  label,
  value,
  onChange,
  required = true,
  multiple = true,
  error,
  disabled,
}: Props) {
  const [options, setOptions] = useState<TopicReference[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [retry, setRetry] = useState(0)
  const family = kind === 'familias'
  const singular = family ? 'família' : 'segmentação'

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    const timer = window.setTimeout(
      async () => {
        setLoading(true)
        setLoadError('')
        try {
          const result = await searchTopicReferences(topicId, kind, search, controller.signal)
          if (active) setOptions(result)
        } catch (error) {
          if (active) setLoadError((error as Error).message)
        } finally {
          if (active) setLoading(false)
        }
      },
      search ? 300 : 0
    )
    return () => {
      active = false
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [topicId, kind, search, retry])

  const mergedOptions = useMemo(() => {
    const byId = new Map(value.map((reference) => [reference.id, reference]))
    options.forEach((reference) => byId.set(reference.id, reference))
    return Array.from(byId.values())
  }, [options, value])

  return (
    <Box sx={{minWidth: 0}}>
      <Autocomplete<TopicReference, boolean, false, false>
        multiple={multiple}
        renderTags={() => null}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        size='small'
        options={mergedOptions}
        value={multiple ? value : value[0] || null}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(reference) => reference.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_event, selected) =>
          onChange(Array.isArray(selected) ? selected : selected ? [selected] : [])
        }
        onInputChange={(_event, input, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(input)
        }}
        noOptionsText={family ? 'Nenhuma família encontrada.' : 'Nenhuma segmentação encontrada.'}
        loadingText={family ? 'Carregando famílias...' : 'Carregando segmentações...'}
        sx={{
          minWidth: 0,
          '& .MuiAutocomplete-inputRoot': {minWidth: 0},
          '& .MuiAutocomplete-input': {minWidth: '60px !important'},
        }}
        renderOption={(props, reference, {selected}) => (
          <li {...props} key={reference.id}>
            {multiple && (
              <Checkbox
                checked={selected}
                size='small'
                tabIndex={-1}
                disableRipple
                inputProps={{'aria-hidden': true}}
                sx={{mr: 1, flexShrink: 0, pointerEvents: 'none'}}
              />
            )}
            <Box sx={{minWidth: 0, py: 0.5}}>
              <Typography variant='body2' fontWeight={500} sx={{overflowWrap: 'anywhere'}}>
                {reference.name}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{overflowWrap: 'anywhere'}}>
                {reference.source === 'solicitacao'
                  ? formatTopicRequestLabel(reference) +
                    ', realizada por ' +
                    formatTopicRequestAuthor(reference)
                  : referenceSource(reference) + ' · Referência local: ' + reference.referenceId}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder='Buscar pelo nome ou referência...'
            error={!!error}
            helperText={
              error ||
              (family
                ? 'Adicione uma ou mais famílias para alteração. Use uma família de briefing ou de uma solicitação de criação salva.'
                : 'Selecione uma solicitação de criação de segmentação salva no sistema (opcional).')
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color='inherit' size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {loadError && (
        <Alert
          severity='error'
          sx={{mt: 1.5}}
          action={
            <Button color='inherit' size='small' onClick={() => setRetry((current) => current + 1)}>
              Tentar novamente
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {value.length > 0 && (
        <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 2, mb: 1}}>
          {value.length} {value.length === 1 ? singular + ' selecionada' : 'famílias selecionadas'}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: value.length > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
          },
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {value.map((reference) => (
          <Box
            key={reference.id}
            sx={{
              position: 'relative',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 0,
              borderRadius: 2,
              bgcolor: '#f8fafc',
            }}
          >
            <IconButton
              aria-label={'Remover ' + singular + ' ' + reference.name}
              size='small'
              disabled={disabled}
              onClick={() => onChange(value.filter((item) => item.id !== reference.id))}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <X size={16} />
            </IconButton>
            <TopicReferenceSummary reference={reference} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}
